import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WhatsAppFab from "@/components/WhatsAppFab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Journal() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/articles`);
                if (alive) setItems(data || []);
            } catch {
                if (alive) setItems([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="journal-page">
            <SiteNav />
            <main className="pt-28 md:pt-36 pb-24">
                <section className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                    <div className="mb-14 md:mb-20 max-w-3xl">
                        <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                            Journal
                        </p>
                        <h1 className="font-serif text-5xl md:text-7xl lg:text-[96px] text-cafe-espresso leading-[0.92] text-balance" style={{ fontWeight: 500 }}>
                            Notes from the <em className="font-display italic text-cafe-olive">studio floor</em>.
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-cafe-espresso/70 leading-relaxed">
                            Tips for cafe owners, behind-the-scenes from shoots, and small ideas about good food and slow images.
                        </p>
                    </div>

                    {loading ? (
                        <div className="py-16 text-center text-cafe-espresso/60">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="py-20 text-center border-t border-cafe-espresso/15">
                            <div className="font-serif text-3xl md:text-4xl text-cafe-espresso" style={{ fontWeight: 500 }}>First post coming soon</div>
                            <p className="mt-4 text-cafe-espresso/60 max-w-md mx-auto">
                                Kaily is writing the first article. Check back soon — or follow <a href="https://instagram.com/kaily_gan" target="_blank" rel="noreferrer" className="text-cafe-olive underline underline-offset-4">@kaily_gan</a> for updates.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 md:gap-y-16" data-testid="articles-grid">
                            {items.map((a) => (
                                <Link
                                    key={a.id}
                                    to={`/journal/${a.slug}`}
                                    className="group block"
                                    data-testid={`article-card-${a.slug}`}
                                >
                                    <div className="aspect-[4/5] overflow-hidden bg-cafe-olive">
                                        {a.cover_image ? (
                                            <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-cafe-cream/70 font-display italic text-sm">
                                                Cover image
                                            </div>
                                        )}
                                    </div>
                                    <div className="pt-5">
                                        {a.tags && a.tags.length > 0 && (
                                            <p className="text-[11px] uppercase tracking-[0.26em] text-cafe-espresso/60 font-medium">
                                                {a.tags.slice(0, 2).join(" · ")}
                                            </p>
                                        )}
                                        <h2 className="mt-2 font-serif text-2xl md:text-3xl text-cafe-espresso leading-tight" style={{ fontWeight: 500 }}>{a.title}</h2>
                                        {a.excerpt && (
                                            <p className="mt-2 text-[14px] text-cafe-espresso/70 line-clamp-3 leading-relaxed">{a.excerpt}</p>
                                        )}
                                        <span className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.26em] text-cafe-espresso/80 border-b border-cafe-espresso/25 pb-0.5">
                                            Read article <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            </main>
            <SiteFooter />
            <WhatsAppFab />
        </div>
    );
}
